import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '../../context/SiteSettingsContext';

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { settings, loading } = useSiteSettings();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Sayfa başlığını dinamik olarak ayarla
  useEffect(() => {
    if (settings.siteName && settings.siteTitle) {
      document.title = `${settings.siteName} - ${settings.siteTitle}`;
    }
  }, [settings.siteName, settings.siteTitle]);

  // Google Analytics'i ekle
  useEffect(() => {
    if (settings.googleAnalyticsId) {
      // Google Analytics script'ini ekle
      const script1 = document.createElement('script');
      script1.async = true;
      script1.src = `https://www.googletagmanager.com/gtag/js?id=${settings.googleAnalyticsId}`;
      document.head.appendChild(script1);

      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${settings.googleAnalyticsId}');
      `;
      document.head.appendChild(script2);

      return () => {
        document.head.removeChild(script1);
        document.head.removeChild(script2);
      };
    }
  }, [settings.googleAnalyticsId]);

  // Loading state - hook'lardan sonra
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Site yükleniyor...</p>
        </div>
      </div>
    );
  }

  const stats = [
    { number: "50.000+", label: "Aktif Öğrenci", icon: "🎓" },
    { number: "2.500+", label: "Uzman Öğretmen", icon: "👨‍🏫" },
    { number: "250+", label: "Ders Kategorisi", icon: "📚" },
    { number: "%98", label: "Memnuniyet Oranı", icon: "⭐" }
  ];

  const features = [
    {
      icon: "🎯",
      title: "Kişiselleştirilmiş Eğitim",
      description: "Her öğrencinin benzersiz ihtiyaçlarına uygun, özel olarak tasarlanmış eğitim programları",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: "👨‍🏫",
      title: "Alanında Uzman Öğretmenler",
      description: "Deneyimli ve uzman öğretmen kadromuz ile kaliteli eğitim garantisi",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: "📊",
      title: "Detaylı İlerleme Takibi",
      description: "Öğrenci performansını anlık takip eden gelişmiş raporlama sistemi",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: "💻",
      title: "Online & Yüz Yüze Eğitim",
      description: "İster online, ister yüz yüze esnek ders seçenekleri ile her yerde eğitim",
      color: "from-orange-500 to-red-500"
    },
    {
      icon: "🏆",
      title: "Sınav Hazırlık Uzmanı",
      description: "YKS, LGS, KPSS ve tüm sınavlara özel hazırlık programları",
      color: "from-indigo-500 to-blue-500"
    },
    {
      icon: "🔒",
      title: "Güvenli Platform",
      description: "Tüm verileriniz en yüksek güvenlik standartları ile korunmaktadır",
      color: "from-teal-500 to-cyan-500"
    }
  ];

  const subjects = [
    "Matematik", "Fizik", "Kimya", "Biyoloji", "Türkçe", "Edebiyat",
    "İngilizce", "Almanca", "Fransızca", "Tarih", "Coğrafya", "Felsefe",
    "Piyano", "Gitar", "Keman", "Resim", "Satranç", "Kodlama"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed w-full top-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-white/95 backdrop-blur-xl shadow-xl shadow-gray-900/5' : 'bg-white/80 backdrop-blur-xl'
      }`}>
        <nav className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-18">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  {settings.siteLogo ? (
                    <img
                      src={`${process.env.REACT_APP_API_URL}${settings.siteLogo}`}
                      alt={settings.siteName}
                      className="w-12 h-12 object-contain"
                    />
                  ) : (
                    <>
                      <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/25">
                        <span className="text-white font-bold text-2xl">E</span>
                      </div>
                      <div className="absolute -inset-1 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl blur opacity-20"></div>
                    </>
                  )}
                </div>
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">{settings.siteName}</h1>
                  <p className="text-sm text-gray-500 font-medium">{settings.siteTitle}</p>
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-10">
              <a href="#features" className="text-gray-600 hover:text-indigo-600 transition-colors font-medium text-sm relative group">
                Özellikler
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-indigo-600 transition-all group-hover:w-full"></span>
              </a>
              <a href="#how-it-works" className="text-gray-600 hover:text-indigo-600 transition-colors font-medium text-sm relative group">
                Nasıl Çalışır
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-indigo-600 transition-all group-hover:w-full"></span>
              </a>
              <a href="#contact" className="text-gray-600 hover:text-indigo-600 transition-colors font-medium text-sm relative group">
                İletişim
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-indigo-600 transition-all group-hover:w-full"></span>
              </a>
              <div className="flex items-center space-x-4 ml-6">
                <Link
                  to="/student/login"
                  className="px-6 py-2.5 text-indigo-600 border border-indigo-200 rounded-xl hover:bg-indigo-50 hover:border-indigo-300 transition-all font-medium text-sm"
                >
                  Öğrenci
                </Link>
                <Link
                  to="/teacher/login"
                  className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-indigo-500/25 transition-all font-medium text-sm"
                >
                  Öğretmen
                </Link>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-600 hover:text-indigo-600 focus:outline-none transition-colors"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-100 py-6 bg-white/95 backdrop-blur-xl">
              <div className="space-y-4 px-6">
                <a href="#features" className="block text-gray-600 hover:text-indigo-600 transition-colors font-medium">
                  Özellikler
                </a>
                <a href="#how-it-works" className="block text-gray-600 hover:text-indigo-600 transition-colors font-medium">
                  Nasıl Çalışır
                </a>
                <a href="#contact" className="block text-gray-600 hover:text-indigo-600 transition-colors font-medium">
                  İletişim
                </a>
                <div className="pt-6 space-y-3">
                  <Link
                    to="/student/login"
                    className="block w-full text-center px-6 py-3 text-indigo-600 border border-indigo-200 rounded-xl hover:bg-indigo-50 transition-all font-medium"
                  >
                    Öğrenci Girişi
                  </Link>
                  <Link
                    to="/teacher/login"
                    className="block w-full text-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all font-medium"
                  >
                    Öğretmen Girişi
                  </Link>
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative pt-16 pb-16 lg:pt-20 lg:pb-20 overflow-hidden bg-white">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(99,102,241,0.3) 1px, transparent 0)', backgroundSize: '20px 20px'}}></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          {/* Badge */}
          <div className="inline-flex items-center px-4 py-2 bg-indigo-50 text-indigo-600 rounded-full text-sm font-medium border border-indigo-100 mb-8">
            <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full mr-2"></span>
            Türkiye'nin Güvenilir Eğitim Platformu
          </div>

          {/* Main Content */}
          <div className="space-y-6 mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
              <span className="block">{settings.siteName || 'EduPlatform'}</span>
              <span className="block text-indigo-600 mt-1">{settings.siteTitle || 'Eğitimde Yeni Nesil'}</span>
            </h1>

            <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {settings.siteDescription || 'Alanında uzman öğretmenlerle birebir eğitim alın. Kişisel gelişiminizi hızlandırın, hedeflerinize ulaşın.'}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-12">
            <Link
              to="/student/login"
              className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm hover:shadow-md"
            >
              Hemen Başla
            </Link>

            <Link
              to="/teacher/login"
              className="px-6 py-3 text-gray-700 font-medium rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
            >
              Öğretmen Ol
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 max-w-md mx-auto">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 mb-1">{settings.studentCount >= 1000 ? `${Math.floor(settings.studentCount / 1000)}K+` : `${settings.studentCount}+`}</div>
              <div className="text-sm text-gray-500">Öğrenci</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 mb-1">{settings.teacherCount >= 1000 ? `${Math.floor(settings.teacherCount / 1000)}K+` : `${settings.teacherCount}+`}</div>
              <div className="text-sm text-gray-500">Öğretmen</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 mb-1">%{settings.satisfactionRate}</div>
              <div className="text-sm text-gray-500">Memnuniyet</div>
            </div>
          </div>
        </div>
      </section>




      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Neden {settings.siteName || 'EduPlatform'}?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Modern eğitim anlayışıyla öğrenme deneyiminizi üst seviyeye taşıyoruz
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group text-center p-8 rounded-3xl bg-gradient-to-b from-indigo-50 to-purple-50 hover:shadow-xl transition-all duration-300">
              <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-indigo-500/25">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Kişiselleştirilmiş Eğitim</h3>
              <p className="text-gray-600">Her öğrencinin ihtiyacına özel tasarlanmış eğitim programları ile maksimum verimlilik</p>
            </div>

            <div className="group text-center p-8 rounded-3xl bg-gradient-to-b from-emerald-50 to-teal-50 hover:shadow-xl transition-all duration-300">
              <div className="w-20 h-20 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-emerald-500/25">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Uzman Öğretmenler</h3>
              <p className="text-gray-600">Alanında deneyimli ve sertifikalı öğretmenlerle kaliteli eğitim garantisi</p>
            </div>

            <div className="group text-center p-8 rounded-3xl bg-gradient-to-b from-rose-50 to-pink-50 hover:shadow-xl transition-all duration-300">
              <div className="w-20 h-20 bg-gradient-to-r from-rose-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-rose-500/25">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">İlerleme Takibi</h3>
              <p className="text-gray-600">Detaylı raporlarla öğrenme sürecinizi takip edin ve gelişiminizi görün</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sosyal Medya Section */}
      {(settings.facebookUrl || settings.twitterUrl || settings.instagramUrl || settings.youtubeUrl || settings.linkedinUrl) && (
        <section className="py-16 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Bizi Takip Edin</h2>
            <p className="text-lg text-gray-600 mb-12">Sosyal medya hesaplarımızdan güncel içerikleri takip edebilirsiniz</p>

            <div className="flex justify-center space-x-6">
              {settings.facebookUrl && (
                <a
                  href={settings.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110"
                >
                  <svg className="w-8 h-8 text-blue-600 group-hover:text-blue-700" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              )}

              {settings.twitterUrl && (
                <a
                  href={settings.twitterUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110"
                >
                  <svg className="w-8 h-8 text-blue-400 group-hover:text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>
              )}

              {settings.instagramUrl && (
                <a
                  href={settings.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110"
                >
                  <svg className="w-8 h-8 text-pink-600 group-hover:text-pink-700" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/>
                    <circle cx="12" cy="12" r="3.5"/>
                    <circle cx="17.5" cy="6.5" r="1.5"/>
                  </svg>
                </a>
              )}

              {settings.youtubeUrl && (
                <a
                  href={settings.youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110"
                >
                  <svg className="w-8 h-8 text-red-600 group-hover:text-red-700" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              )}

              {settings.linkedinUrl && (
                <a
                  href={settings.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110"
                >
                  <svg className="w-8 h-8 text-blue-700 group-hover:text-blue-800" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* İletişim Section */}
      <section id="contact-section" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              İletişime Geçin
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Sorularınız için bize ulaşın. Size en kısa sürede dönüş yapacağız.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Email */}
            <div className="text-center p-8 bg-gradient-to-b from-blue-50 to-indigo-50 rounded-3xl hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">E-posta</h3>
              <p className="text-gray-600 mb-4">Size en kısa sürede yanıt vereceğiz</p>
              <a href={`mailto:${settings.contactEmail}`} className="text-indigo-600 hover:text-indigo-700 font-medium">
                {settings.contactEmail}
              </a>
            </div>

            {/* Phone */}
            <div className="text-center p-8 bg-gradient-to-b from-emerald-50 to-green-50 rounded-3xl hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-green-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Telefon</h3>
              <p className="text-gray-600 mb-4">Hemen arayın ve destek alın</p>
              <a href={`tel:${settings.contactPhone}`} className="text-emerald-600 hover:text-emerald-700 font-medium">
                {settings.contactPhone}
              </a>
            </div>

            {/* Address */}
            <div className="text-center p-8 bg-gradient-to-b from-purple-50 to-pink-50 rounded-3xl hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Adres</h3>
              <p className="text-gray-600 mb-4">Ofisimizi ziyaret edebilirsiniz</p>
              <p className="text-purple-600 font-medium">
                {settings.contactAddress}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nasıl Çalışır?
            </h2>
            <p className="text-lg text-gray-600">
              3 basit adımda eğitime başlayın
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="relative text-center group">
              <div className="w-24 h-24 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl flex items-center justify-center mx-auto mb-8 text-white text-3xl font-bold shadow-xl shadow-indigo-500/25 group-hover:scale-110 transition-transform">
                1
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Takip</h3>
              <p className="text-gray-600 leading-relaxed">
               Eğitim Danışmanının verdiği kullanıcı adı ve şifresi ile takip edin.
              </p>
              <div className="hidden md:block absolute top-12 -right-6 w-12 h-0.5 bg-gradient-to-r from-indigo-300 to-purple-300"></div>
            </div>

            <div className="relative text-center group">
              <div className="w-24 h-24 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl flex items-center justify-center mx-auto mb-8 text-white text-3xl font-bold shadow-xl shadow-emerald-500/25 group-hover:scale-110 transition-transform">
                2
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Uzman Öğretmen</h3>
              <p className="text-gray-600 leading-relaxed">
                Tüm seviyelerde alanında uzman öğretmenlerle ders.
              </p>
              <div className="hidden md:block absolute top-12 -right-6 w-12 h-0.5 bg-gradient-to-r from-emerald-300 to-teal-300"></div>
            </div>

            <div className="text-center group">
              <div className="w-24 h-24 bg-gradient-to-r from-rose-500 to-pink-500 rounded-3xl flex items-center justify-center mx-auto mb-8 text-white text-3xl font-bold shadow-xl shadow-rose-500/25 group-hover:scale-110 transition-transform">
                3
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Eğitime Başlayın</h3>
              <p className="text-gray-600 leading-relaxed">
                Online veya yüz yüze derslerinize hemen başlayın
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Eğitim Yolculuğunuza Başlayın
          </h2>
          <p className="text-xl text-indigo-100 mb-10">
            Binlerce öğrenci gibi siz de hedeflerinize ulaşın
          </p>
          <Link
            to="/student/login"
            className="inline-flex items-center px-8 py-4 bg-white text-indigo-600 font-semibold rounded-2xl hover:shadow-2xl hover:shadow-white/20 transition-all transform hover:scale-105"
          >
            Hemen Başlayın
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      {settings.contactPhone && (
        <div className="fixed bottom-6 right-6 z-50">
          <a
            href={`https://wa.me/${settings.contactPhone.replace(/[^0-9]/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl hover:shadow-green-500/50 transition-all duration-300 transform hover:scale-110 flex items-center justify-center"
            title="WhatsApp ile İletişime Geç"
          >
            {/* WhatsApp Icon */}
            <svg
              className="w-8 h-8"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
            </svg>

            {/* Pulse Animation */}
            <div className="absolute inset-0 rounded-full bg-green-500 opacity-75 animate-ping"></div>
          </a>
        </div>
      )}

      {/* Footer */}
      <footer id="contact" className="bg-gray-900 text-white py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            {/* Logo & Description */}
            <div className="md:col-span-1">
              <div className="flex items-center space-x-4 mb-6">
                <div className="relative">
                  {settings.siteLogo ? (
                    <img
                      src={`${process.env.REACT_APP_API_URL}${settings.siteLogo}`}
                      alt={settings.siteName}
                      className="w-12 h-12 object-contain"
                    />
                  ) : (
                    <>
                      <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center">
                        <span className="text-white font-bold text-2xl">E</span>
                      </div>
                      <div className="absolute -inset-1 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl blur opacity-20"></div>
                    </>
                  )}
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white">{settings.siteName}</h4>
                  <p className="text-sm text-gray-400">{settings.siteTitle}</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                {settings.footerText || settings.siteDescription || 'Modern eğitim anlayışıyla binlerce öğrenci ve öğretmeni buluşturan güvenilir platform.'}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h5 className="text-lg font-semibold mb-6 text-white">Hızlı Linkler</h5>
              <ul className="space-y-3">
                <li>
                  <a href="#features" className="text-gray-400 hover:text-indigo-400 transition-colors text-sm">
                    Özellikler
                  </a>
                </li>
                <li>
                  <a href="#how-it-works" className="text-gray-400 hover:text-indigo-400 transition-colors text-sm">
                    Nasıl Çalışır
                  </a>
                </li>
                <li>
                  <Link to="/student/login" className="text-gray-400 hover:text-indigo-400 transition-colors text-sm">
                    Öğrenci Girişi
                  </Link>
                </li>
                <li>
                  <Link to="/teacher/login" className="text-gray-400 hover:text-indigo-400 transition-colors text-sm">
                    Öğretmen Girişi
                  </Link>
                </li>
              </ul>
            </div>

            {/* Partner Sites */}
            <div>
              <h5 className="text-lg font-semibold mb-6 text-white">Sayfalar</h5>
              <ul className="space-y-3">
                <li>
                  <a href="https://www.populerhoca.com/populer-hoca-hizli-okuma-kursu/" target="_blank" className="text-gray-400 hover:text-indigo-400 transition-colors text-sm">
                    Popüler Hoca Hızlı Okuma Kursu
                  </a>
                </li>
                <li>
                  <a href="https://www.populerhoca.com/populer-hoca-matematik-ozel-ders/" target="_blank" className="text-gray-400 hover:text-indigo-400 transition-colors text-sm">
                    Popüler Hoca Matematik Özel Ders
                  </a>
                </li>
                <li>
                  <a href="https://www.ozeldersdunyasi.com/" target="_blank" className="text-gray-400 hover:text-indigo-400 transition-colors text-sm">
                    Özel Ders Dünyası
                  </a>
                </li>
                <li>
                  <a href="https://www.populerakademi.com/" target="_blank" className="text-gray-400 hover:text-indigo-400 transition-colors text-sm">
                    Popüler Akademi
                  </a>
                </li>
                <li>
                  <a href="https://matematikozelders.com.tr/" target="_blank" className="text-gray-400 hover:text-indigo-400 transition-colors text-sm">
                    Matematik Özel Ders
                  </a>
                </li>
                <li>
                  <a href="https://okuyabilirsin.com/" target="_blank" className="text-gray-400 hover:text-indigo-400 transition-colors text-sm">
                    Okuyabilirsin
                  </a>
                </li>
                <li>
                  <a href="https://okumaks.com/" target="_blank" className="text-gray-400 hover:text-indigo-400 transition-colors text-sm">
                    Okumaks
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact & Social */}
            <div>
              <h5 className="text-lg font-semibold mb-6 text-white">İletişim</h5>
              <div className="space-y-4 mb-6">
                <div className="flex items-center text-gray-400 text-sm">
                  <svg className="w-4 h-4 mr-3 text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  {settings.contactEmail}
                </div>
                <div className="flex items-center text-gray-400 text-sm">
                  <svg className="w-4 h-4 mr-3 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  {settings.contactPhone}
                </div>
                <div className="flex items-center text-gray-400 text-sm">
                  <svg className="w-4 h-4 mr-3 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  {settings.contactAddress}
                </div>
              </div>

              <h6 className="text-sm font-semibold mb-4 text-white">Bizi Takip Edin</h6>
              <div className="flex space-x-3 mb-6">
                {settings.facebookUrl && (
                  <a href={settings.facebookUrl} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center hover:bg-blue-600 transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                )}
                {settings.twitterUrl && (
                  <a href={settings.twitterUrl} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center hover:bg-blue-400 transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                    </svg>
                  </a>
                )}
                {settings.instagramUrl && (
                  <a href={settings.instagramUrl} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-600 transition-all">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/>
                      <circle cx="12" cy="12" r="3.5"/>
                      <circle cx="17.5" cy="6.5" r="1.5"/>
                    </svg>
                  </a>
                )}
                {settings.youtubeUrl && (
                  <a href={settings.youtubeUrl} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center hover:bg-red-600 transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                )}
              </div>

              {(settings.appStoreUrl || settings.googlePlayUrl) && (
                <div>
                  <p className="text-sm text-gray-400 mb-3">Mobil Uygulamamız</p>
                  <div className="flex space-x-2">
                    {settings.appStoreUrl && (
                      <a href={settings.appStoreUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-2 bg-gray-800 rounded-xl hover:bg-gray-700 transition-colors text-xs">
                        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                        </svg>
                        <span>App Store</span>
                      </a>
                    )}
                    {settings.googlePlayUrl && (
                      <a href={settings.googlePlayUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-2 bg-gray-800 rounded-xl hover:bg-gray-700 transition-colors text-xs">
                        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/>
                        </svg>
                        <span>Google Play</span>
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Bottom */}
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
              <p className="text-gray-400 text-sm">
                {settings.footerCopyright}
              </p>
              <div className="flex space-x-6 mt-4 md:mt-0">
                <a href="#" className="text-gray-400 hover:text-indigo-400 text-sm transition-colors">
                  Gizlilik Politikası
                </a>
                <a href="#" className="text-gray-400 hover:text-indigo-400 text-sm transition-colors">
                  Kullanım Koşulları
                </a>
                <a href="#" className="text-gray-400 hover:text-indigo-400 text-sm transition-colors">
                  KVKK
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}